/**
 * Collection redirect bar.
 *
 * The bar itself is plain links - navigating to another collection is a normal
 * page load. Two things need scripting, both about the mobile scroller:
 *
 *   - when the active collection sits off-screen, bring it into view so
 *     shoppers can see where they are;
 *   - tell the stylesheet which ends still have collections past them, so it
 *     can fade that edge out under a chevron. The scrollbar is hidden, so
 *     without the cue there is nothing on a phone to say the row scrolls;
 *     pressing one scrolls the row that way.
 */
(function () {
  var SELECTOR_ROOT = '[data-collection-redirect-bar]';
  var SELECTOR_NAV = '[data-collection-redirect-nav]';
  var SELECTOR_ACTIVE = '.collection-redirect-bar__link.is-active';
  var SELECTOR_SCROLLER = '[data-crb-scroller]';
  var SELECTOR_CUE = '[data-crb-cue]';

  /* Sub-pixel layout routinely leaves a fraction of a pixel of "overflow" that
     nobody can scroll to, and a scroller resting at its end can report a
     scrollLeft a hair under the maximum. Anything inside a pixel is nothing. */
  var EPSILON = 1;

  function revealActiveLink(root) {
    if (!root) return;

    var nav = root.querySelector(SELECTOR_NAV);
    if (!nav) return;

    var active = nav.querySelector(SELECTOR_ACTIVE);
    if (!active) return;

    // Nothing to scroll when every link already fits.
    var maxScroll = nav.scrollWidth - nav.clientWidth;
    if (maxScroll <= 0) return;

    // Measured off bounding rects rather than offsetLeft: the links sit inside
    // a positioned wrapper, so offsetLeft is not relative to the scroller.
    var navRect = nav.getBoundingClientRect();
    var activeRect = active.getBoundingClientRect();
    var currentOffset = activeRect.left - navRect.left;
    var wantedOffset = (nav.clientWidth - activeRect.width) / 2;
    var target = nav.scrollLeft + currentOffset - wantedOffset;

    nav.scrollLeft = Math.max(0, Math.min(target, maxScroll));
  }

  function updateCues(root) {
    if (!root) return;

    var scroller = root.querySelector(SELECTOR_SCROLLER);
    var nav = root.querySelector(SELECTOR_NAV);
    if (!scroller || !nav) return;

    var maxScroll = nav.scrollWidth - nav.clientWidth;

    if (maxScroll <= EPSILON) {
      scroller.classList.remove('is-overflow-start');
      scroller.classList.remove('is-overflow-end');
      return;
    }

    scroller.classList.toggle('is-overflow-start', nav.scrollLeft > EPSILON);
    scroller.classList.toggle('is-overflow-end', nav.scrollLeft < maxScroll - EPSILON);
  }

  /* Just under a screenful, so the link that was half under the cue is fully
     in view afterwards and a little of what was already read stays on screen
     to anchor the jump. */
  function scrollOneScreen(nav, direction) {
    var step = Math.max(120, Math.round(nav.clientWidth * 0.8)) * direction;

    /* Honoured rather than assumed: someone who has asked the system for less
       motion should not be given a long animated slide. */
    var reduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!reduced && typeof nav.scrollBy === 'function') {
      try {
        nav.scrollBy({ left: step, behavior: 'smooth' });
        return;
      } catch (error) {
        /* Older Safari takes scrollBy(x, y) but not the options object, and
           throws on it. Fall through to setting the offset outright. */
      }
    }

    nav.scrollLeft = nav.scrollLeft + step;
  }

  function setup(root) {
    revealActiveLink(root);

    var nav = root.querySelector(SELECTOR_NAV);
    if (!nav) return;

    Array.prototype.forEach.call(root.querySelectorAll(SELECTOR_CUE), function (cue) {
      cue.addEventListener('click', function () {
        scrollOneScreen(nav, cue.getAttribute('data-crb-cue') === 'start' ? -1 : 1);
      });
    });

    /* Coalesced into a frame: scroll fires far faster than the cue can
       usefully change, and the read below forces layout. */
    var queued = false;
    function refresh() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        updateCues(root);
      });
    }

    nav.addEventListener('scroll', refresh, { passive: true });

    /* The row's width moves without the window resizing - a webfont landing
       reflows every label. Observing the track catches that; observing the nav
       catches the viewport. Both die with the section when the theme editor
       replaces it, which is why neither is a window listener. */
    if ('ResizeObserver' in window) {
      var observer = new ResizeObserver(refresh);
      observer.observe(nav);
      if (nav.firstElementChild) observer.observe(nav.firstElementChild);
    }

    updateCues(root);
  }

  function init(scope) {
    var context = scope || document;
    var roots = context.querySelectorAll(SELECTOR_ROOT);

    Array.prototype.forEach.call(roots, setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* One listener for the page rather than one per bar, so a section reloaded
     in the theme editor cannot leave the old one behind on window. */
  window.addEventListener(
    'resize',
    function () {
      Array.prototype.forEach.call(document.querySelectorAll(SELECTOR_ROOT), updateCues);
    },
    { passive: true }
  );

  // Theme editor: re-centre and re-measure after the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
